import React, { useState, useContext, useMemo, useCallback } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    Chip,
    InputAdornment,
    Dialog,
    DialogContent,
    Alert,
    Tabs,
    Tab,
    CircularProgress,
    Paper,
    Card,
    styled,
    useTheme,
} from '@mui/material';
import {
    Search as SearchIcon,
    AttachMoney as MoneyIcon,
    AccountBalance as BalanceIcon,
    Receipt as ReceiptIcon,
    Refresh as RefreshIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    ArrowBack as ArrowBackIcon,
    FilterAlt as FilterAltIcon,
    CreditCard as CreditCardIcon,
    ReceiptLong as ReceiptLongIcon,
    Diamond as DiamondIcon,
    Numbers as NumbersIcon,
    Whatshot as WhatshotIcon,
    LocalAtm as LocalAtmIcon,
    Business as BusinessIcon,
    Inventory as InventoryIcon,
    Scale as ScaleIcon,
    Paid as PaidIcon,
    Pending as PendingIcon,
    Assessment as AssessmentIcon,
    Description as DescriptionIcon,
    Event as EventIcon,
    Info as InfoIcon,
    Sort as SortIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DataContext } from '../../context/DataContext';



// Styled components using Material-UI
const GlassCard = styled(Card)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'light'
        ? 'rgba(255, 255, 255, 0.85)'
        : 'rgba(30, 41, 59, 0.85)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: `1px solid ${theme.palette.mode === 'light'
        ? 'rgba(255, 255, 255, 0.3)'
        : 'rgba(148, 163, 184, 0.1)'}`,
    borderRadius: 20,
    boxShadow: theme.shadows[4],
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',
    position: 'relative',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: theme.palette.mode === 'light'
            ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 100%)',
        pointerEvents: 'none',
        borderRadius: 20,
    },
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: theme.shadows[6],
        borderColor: theme.palette.mode === 'light'
            ? 'rgba(255, 255, 255, 0.5)'
            : 'rgba(148, 163, 184, 0.2)',
    }
}));

const GlassPanel = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'light'
        ? 'rgba(255, 255, 255, 0.7)'
        : 'rgba(30, 41, 59, 0.7)',
    backdropFilter: 'blur(40px)',
    WebkitBackdropFilter: 'blur(40px)',
    border: `1px solid ${theme.palette.mode === 'light'
        ? 'rgba(255, 255, 255, 0.3)'
        : 'rgba(148, 163, 184, 0.1)'}`,
    borderRadius: 24,
    boxShadow: theme.shadows[4],
}));

const SupplierItem = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(1.5),
    cursor: 'pointer',
    border: `2px solid ${active
        ? (theme.palette.mode === 'light' ? 'rgba(37, 99, 235, 0.4)' : 'rgba(96, 165, 250, 0.4)')
        : 'transparent'}`,
    borderRadius: 20,
    backgroundColor: active
        ? theme.palette.mode === 'light'
            ? 'linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(29, 78, 216, 0.05) 100%)'
            : 'linear-gradient(135deg, rgba(96, 165, 250, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)'
        : theme.palette.mode === 'light'
            ? 'rgba(255, 255, 255, 0.85)'
            : 'rgba(30, 41, 59, 0.85)',
    transition: 'all 0.3s ease',
    '&:hover': {
        borderColor: theme.palette.mode === 'light'
            ? 'rgba(37, 99, 235, 0.2)'
            : 'rgba(96, 165, 250, 0.2)',
        backgroundColor: theme.palette.mode === 'light'
            ? 'rgba(255, 255, 255, 0.95)'
            : 'rgba(30, 41, 59, 0.95)',
    }
}));

const SupplierAvatar = styled(Box)(({ theme }) => ({
    width: 56,
    height: 56,
    borderRadius: 16,
    background: theme.palette.mode === 'light'
        ? 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)'
        : 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.common.white,
    marginRight: theme.spacing(2),
    flexShrink: 0,
    fontSize: 24,
}));

const StatCard = styled(GlassCard)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
}));

// const StatIcon = styled(Box, {
//     shouldForwardProp: (prop) => prop !== 'colorType',
// })(({ theme, colorType }) => ({
//     width: 45,
//     height: 45,
//     borderRadius: 16,
//     display: 'flex',
//     alignItems: 'center',
//     justifyContent: 'center',
//     color: theme.palette.common.white,
//     marginRight: theme.spacing(2),
//     flexShrink: 0,
//     fontSize: 24,
//     boxShadow: theme.shadows[4],
//     background: colorType === 'primary'
//         ? (theme.palette.mode === 'light'
//             ? 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)'
//             : 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)')
//         : colorType === 'success'
//             ? (theme.palette.mode === 'light'
//                 ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
//                 : 'linear-gradient(135deg, #34D399 0%, #10B981 100%)')
//             : colorType === 'warning'
//                 ? (theme.palette.mode === 'light'
//                     ? 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)'
//                     : 'linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)')
//                 : (theme.palette.mode === 'light'
//                     ? 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)'
//                     : 'linear-gradient(135deg, #A78BFA 0%, #8B5CF6 100%)'),
// }));

const TransactionItem = styled(GlassCard, {
    shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
    display: 'flex',
    padding: theme.spacing(2.5),
    cursor: 'pointer',
    border: `2px solid ${active
        ? (theme.palette.mode === 'light' ? 'rgba(16, 185, 129, 0.4)' : 'rgba(52, 211, 153, 0.4)')
        : 'transparent'}`,
    transition: 'all 0.3s ease',
    backgroundColor: active
        ? theme.palette.mode === 'light'
            ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)'
            : 'linear-gradient(135deg, rgba(52, 211, 153, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)'
        : 'inherit',
}));

const TransactionIcon = styled(Box)(({ theme }) => ({
    width: 56,
    height: 56,
    borderRadius: 16,
    background: theme.palette.mode === 'light'
        ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(29, 78, 216, 0.1) 100%)'
        : 'linear-gradient(135deg, rgba(96, 165, 250, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing(2.5),
    flexShrink: 0,
    fontSize: 24,
    border: `1px solid ${theme.palette.mode === 'light'
        ? 'rgba(59, 130, 246, 0.2)'
        : 'rgba(96, 165, 250, 0.2)'}`,
}));

const DetailSection = styled(GlassCard, {
    shouldForwardProp: (prop) => prop !== 'fullWidth',
})(({ theme, fullWidth }) => ({
    padding: theme.spacing(3),
    gridColumn: fullWidth ? '1 / -1' : 'auto',
}));

const PaymentMethod = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2.5),
    borderRadius: 16,
    background: theme.palette.mode === 'light'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(30, 41, 59, 0.3)',
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[2],
    }
}));

const PaymentIcon = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'type',
})(({ theme, type }) => ({
    width: 48,
    height: 48,
    borderRadius: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.common.white,
    fontSize: 24,
    background: type === 'cash'
        ? (theme.palette.mode === 'light'
            ? 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)'
            : 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)')
        : (theme.palette.mode === 'light'
            ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
            : 'linear-gradient(135deg, #34D399 0%, #10B981 100%)'),
}));



const FineItem = styled(GlassCard)(({ theme }) => ({
    padding: theme.spacing(2.5),
}));

const EmptyState = styled(Box)(({ theme }) => ({
    padding: theme.spacing(8, 4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    background: theme.palette.mode === 'light'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(30, 41, 59, 0.3)',
    borderRadius: 20,
    border: `1px dashed ${theme.palette.divider}`,
}));

const FineStat = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    padding: theme.spacing(2),
    background: theme.palette.mode === 'light'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(30, 41, 59, 0.3)',
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
}));


const SupplierLedger = () => {
    const {
        suppliers,
        supplierTransactions,
        loading,
        refresh
    } = useContext(DataContext);

    const theme = useTheme();

    // State for suppliers and transactions
    const error = null;
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedSupplierId, setSelectedSupplierId] = useState(null);
    const [selectedTransactionId, setSelectedTransactionId] = useState(null);
    const [activeTab, setActiveTab] = useState('transactions');
    const [transactionDetailOpen, setTransactionDetailOpen] = useState(false);
    const [selectedTransactionDetail, setSelectedTransactionDetail] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [sortOrder, setSortOrder] = useState('desc');

    function formatDateToDDMMYYYY(dateString) {
        if (!dateString) return '';

        const date = new Date(dateString);

        // Convert to IST: UTC + 5 hours 30 minutes
        const istOffset = 5.5 * 60 * 60 * 1000; // 5.5 hours in milliseconds
        const istDate = new Date(date.getTime() + istOffset);

        // Use UTC methods to get the IST date
        const day = String(istDate.getUTCDate()).padStart(2, '0');
        const month = String(istDate.getUTCMonth() + 1).padStart(2, '0');
        const year = istDate.getUTCFullYear();

        return `${day}/${month}/${year}`;
    }

    // Get suppliers with pending payments
    const suppliersWithPendingPayments = useMemo(() => {
        const pendingSuppliers = suppliers?.filter(supplier => {
            return supplier?.BalanceValueBank > 0 || supplier?.balanceFine > 0 || supplier?.balanceValueCash > 0;
        });

        // Sort alphabetically
        return pendingSuppliers?.sort((a, b) => {
            const nameA = (a.name || '').toLowerCase();
            const nameB = (b.name || '').toLowerCase();
            if (nameA < nameB) return -1;
            if (nameA > nameB) return 1;
            return 0;
        });
    }, [suppliers]);

    // Get selected supplier details
    const selectedSupplier = useMemo(() => {
        return suppliers?.find(s => s.id === selectedSupplierId);
    }, [suppliers, selectedSupplierId]);

    // Get transactions for selected supplier
    const selectedSupplierTransactions = useMemo(() => {
        if (!selectedSupplierId) return [];
        const transactions = supplierTransactions.filter(t => t.supplier === selectedSupplierId);
        return transactions.sort((a, b) => {
            if (a.timestamp && b.timestamp) {
                return b.timestamp - a.timestamp;
            }
            if (a.createdAt && b.createdAt) {
                return new Date(b.createdAt) - new Date(a.createdAt);
            }
            if (a.transactionTime && b.transactionTime) {
                const dateTimeA = new Date(`${a.date} ${a.transactionTime}`);
                const dateTimeB = new Date(`${b.date} ${b.transactionTime}`);
                return dateTimeB - dateTimeA;
            }
            try {
                const dateA = new Date(a.date.split('/').reverse().join('-'));
                const dateB = new Date(b.date.split('/').reverse().join('-'));
                return dateB - dateA;
            } catch {
                return 0;
            }
        });
    }, [selectedSupplierId, supplierTransactions]);

    // Get fine transactions
    const fineTransactions = useMemo(() => {
        if (!selectedSupplierId) return [];
        const transactions = supplierTransactions.filter(t =>
            t.supplier === selectedSupplierId && t.finePayments && t.finePayments?.length > 0
        );
        return transactions;
    }, [selectedSupplierId, supplierTransactions]);

    // Calculate supplier statistics
    const supplierStats = useMemo(() => {
        if (!selectedSupplierId) return null;

        const transactions = selectedSupplierTransactions;
        const totalBuying = transactions.reduce((sum, t) => sum + (t.summary?.grandTotal || 0), 0);
        const totalPaid = transactions.reduce((sum, t) => sum + (t.summary?.paymentDone || 0), 0);
        let totalPending = transactions.reduce((sum, t) => sum + (t.summary?.pendingAmount || 0), 0);
        console.log(transactions)
        const totalFineBuying = transactions.reduce((sum, t) => sum + (t.summary?.fineBalance || 0), 0);
        const totalFinePaid = transactions.reduce((sum, t) => sum + (t.summary?.finePayment || 0), 0);
        const openingBalanceCash = selectedSupplier?.openingBalanceCash || 0;
        const openingBalanceBank = selectedSupplier?.openingBalanceBank || 0;
        const openingBalanceFine = selectedSupplier?.openingBalanceFine || 0;
        const fineBalance = openingBalanceFine + totalFineBuying - totalFinePaid;
        const cashBalance = selectedSupplier?.balanceValueCash || 0;
        const bankBalance = selectedSupplier?.balanceValueBank || 0;
        const openingBalance = openingBalanceCash + openingBalanceBank;
        totalPending += openingBalance;
        return {
            totalBuying,
            totalPaid,
            totalPending,
            totalTransactions: transactions?.length,
            openingBalance,
            openingBalanceCash,
            openingBalanceBank,
            openingBalanceFine,
            cashBalance,
            bankBalance,
            totalFineBuying,
            totalFinePaid,
            fineBalance
        };
    }, [selectedSupplierId, selectedSupplierTransactions, selectedSupplier?.openingBalanceCash,
        selectedSupplier?.openingBalanceBank,
        selectedSupplier?.openingBalanceFine,
        selectedSupplier?.balanceValueCash,
        selectedSupplier?.balanceValueBank]);

    // Filtered suppliers based on search with sorting
    const filteredSuppliers = useMemo(() => {
        let filtered = suppliersWithPendingPayments || [];

        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            filtered = filtered.filter(supplier =>
                supplier.name?.toLowerCase().includes(query) ||
                supplier.contact.toString()?.toLowerCase().includes(query) ||
                supplier.address?.toLowerCase().includes(query)
            );
        }

        // Apply sorting based on pending amount
        filtered = [...filtered].sort((a, b) => {
            const pendingA = (a.balanceValueCash || 0) + (a.BalanceValueBank || 0);
            const pendingB = (b.balanceValueCash || 0) + (b.BalanceValueBank || 0);

            if (sortOrder === 'desc') {
                // High to Low: B comes first if higher
                return pendingB - pendingA;
            } else {
                // Low to High: A comes first if lower
                return pendingA - pendingB;
            }
        });

        return filtered;
    }, [suppliersWithPendingPayments, searchQuery, sortOrder]);

    // Handle transaction detail view
    const handleViewTransactionDetail = useCallback((transactionId) => {
        const transaction = supplierTransactions?.find(t => t.id === transactionId);
        if (transaction) {
            setSelectedTransactionDetail(transaction);
            setTransactionDetailOpen(true);
            setSelectedTransactionId(transactionId);
        }
    }, [supplierTransactions]);

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue === 0 ? 'transactions' : 'fine');
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    // Render supplier list
    const renderSupplierList = () => {
        if (loading) {
            return (
                <EmptyState>
                    <CircularProgress size={40} sx={{ mb: 2, color: 'primary.main' }} />
                    Loading suppliers...
                </EmptyState>
            );
        }

        if (filteredSuppliers?.length === 0) {
            return (
                <EmptyState>
                    <InventoryIcon sx={{ fontSize: 56, mb: 2, opacity: 0.5 }} />
                    {searchQuery ? 'No suppliers match your search' : 'No suppliers with pending payments'}
                </EmptyState>
            );
        }

        return filteredSuppliers?.map(supplier => {
            const supplierTxns = supplierTransactions?.filter(t => t.supplier === supplier.id);
            console.log(supplierTxns)
            const totalPendingCash = supplier?.balanceValueCash || 0;
            const totalPendingBank = supplier?.balanceValueBank || 0;
            const totalPending = totalPendingCash + totalPendingBank;
            const totalFinePending = supplier?.balanceFine || 0;
            const transactionCount = supplierTxns?.length;

            return (
                <SupplierItem
                    key={supplier.id}
                    active={selectedSupplierId === supplier.id}
                    onClick={() => setSelectedSupplierId(supplier.id)}
                >
                    <SupplierAvatar>
                        <BusinessIcon />
                    </SupplierAvatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                {supplier.name || 'Unknown Supplier'}
                            </Typography>
                            <Chip
                                label={`${transactionCount} txn`}
                                size="small"
                                sx={{
                                    fontSize: 11,
                                    height: 20,
                                    bgcolor: theme.palette.mode === 'light'
                                        ? 'rgba(37, 99, 235, 0.1)'
                                        : 'rgba(96, 165, 250, 0.1)',
                                    color: 'primary.main',
                                    fontWeight: 600,
                                }}
                            />
                        </Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.25 }}>
                            {supplier.contact || 'No contact'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1.25 }}>
                            <Chip
                                icon={<MoneyIcon />}
                                label={formatCurrency(totalPending)}
                                size="small"
                                sx={{
                                    bgcolor: theme.palette.mode === 'light'
                                        ? 'rgba(37, 99, 235, 0.1)'
                                        : 'rgba(96, 165, 250, 0.1)',
                                    border: `1px solid ${theme.palette.mode === 'light'
                                        ? 'rgba(37, 99, 235, 0.2)'
                                        : 'rgba(96, 165, 250, 0.2)'}`,
                                    color: 'text.primary',
                                    fontWeight: 600,
                                }}
                            />
                            <Chip
                                icon={<ScaleIcon />}
                                label={`${totalFinePending.toFixed(2)}g`}
                                size="small"
                                sx={{
                                    bgcolor: theme.palette.mode === 'light'
                                        ? 'rgba(245, 158, 11, 0.1)'
                                        : 'rgba(251, 191, 36, 0.1)',
                                    border: `1px solid ${theme.palette.mode === 'light'
                                        ? 'rgba(245, 158, 11, 0.2)'
                                        : 'rgba(251, 191, 36, 0.2)'}`,
                                    color: 'text.primary',
                                    fontWeight: 600,
                                }}
                            />
                        </Box>
                    </Box>
                </SupplierItem>
            );
        });
    };

    // Render stats cards
    const renderStatsCards = () => {
        if (!supplierStats) return null;

        const cards = [
            {
                label: 'OPENING BALANCE CASH',
                value: formatCurrency(supplierStats.openingBalanceCash),
                icon: <ReceiptIcon />,
                colorType: 'primary',
                subtitle: `from ${supplierStats.totalTransactions} transactions`
            },
            {
                label: 'OPENING BALANCE BANK',
                value: formatCurrency(supplierStats.openingBalanceBank),
                icon: <ReceiptIcon />,
                colorType: 'primary',
                subtitle: `from ${supplierStats.totalTransactions} transactions`
            },
            {
                label: 'OPENING BALANCE FINE',
                value: `${supplierStats.openingBalanceFine.toFixed(2)}gm`,
                icon: <ReceiptIcon />,
                colorType: 'primary',
                subtitle: `from ${supplierStats.totalTransactions} transactions`
            },
            {
                label: 'TOTAL CASH/BANK BUYING',
                value: formatCurrency(supplierStats.totalBuying),
                icon: <ReceiptIcon />,
                colorType: 'primary',
                subtitle: `from ${supplierStats.totalTransactions} transactions`
            },
            {
                label: 'TOTAL CASH/BANK PAID',
                value: formatCurrency(supplierStats.totalPaid),
                icon: <PaidIcon />,
                colorType: 'success',
                subtitle: `${((supplierStats.totalPaid / supplierStats.totalBuying) * 100).toFixed(1)}% of total`
            },
            {
                label: 'TOTAL FINE BUYING',
                value: `${supplierStats.totalFineBuying.toFixed(2)}gm`,
                icon: <ReceiptIcon />,
                colorType: 'primary',
                subtitle: `from ${supplierStats.totalTransactions} transactions`
            },
            {
                label: 'TOTAL FINE PAID',
                value: `${supplierStats.totalFinePaid.toFixed(2)}gm`,
                icon: <PaidIcon />,
                colorType: 'success',
                subtitle: `${((supplierStats.totalPaid / supplierStats.totalBuying) * 100).toFixed(1)}% of total`
            },
            {
                label: 'PENDING PAYMENT BANK',
                value: formatCurrency(supplierStats.bankBalance),
                icon: <PendingIcon />,
                colorType: 'warning',
                subtitle: `${((supplierStats.totalPending / supplierStats.totalBuying) * 100).toFixed(1)}% of total`
            },
            {
                label: 'PENDING PAYMENT CASH',
                value: formatCurrency(supplierStats.cashBalance),
                icon: <PendingIcon />,
                colorType: 'warning',
                subtitle: `${((supplierStats.totalPending / supplierStats.totalBuying) * 100).toFixed(1)}% of total`
            },
            {
                label: 'PENDING FINE',
                value: `${supplierStats.fineBalance.toFixed(2)}gm`,
                icon: <DiamondIcon />,
                colorType: 'secondary',
                subtitle: 'Fine weight pending'
            }
        ];

        return (
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                gap: 1.5
            }}>
                {cards.map((stat, index) => (
                    <StatCard key={index}>
                        {/* <StatIcon colorType={stat.colorType}>
                            {stat.icon}
                        </StatIcon> */}
                        <Box sx={{ flex: 1, position: 'relative', zIndex: 1 }}>
                            <Typography variant="caption" sx={{
                                color: 'text.secondary',
                                fontWeight: 500,
                                letterSpacing: 0.5,
                                textTransform: 'uppercase',
                                mb: 0.75
                            }}>
                                {stat.label}
                            </Typography>
                            <Typography variant="h6" sx={{
                                fontWeight: 600,
                                color: 'text.primary',
                                mb: 0.5
                            }}>
                                {stat.value}
                            </Typography>
                            <Typography variant="caption" sx={{
                                color: 'text.secondary',
                                fontWeight: 500,
                            }}>
                                {stat.subtitle}
                            </Typography>
                        </Box>
                    </StatCard>
                ))}
            </Box>
        );
    };

    // Render transaction list
    const renderTransactionList = () => {
        if (!selectedSupplierId) {
            return (
                <EmptyState>
                    <DescriptionIcon sx={{ fontSize: 56, mb: 2, opacity: 0.5 }} />
                    Select a supplier to view transactions
                </EmptyState>
            );
        }

        if (selectedSupplierTransactions?.length === 0) {
            return (
                <EmptyState>
                    <ReceiptLongIcon sx={{ fontSize: 56, mb: 2, opacity: 0.5 }} />
                    No transactions found
                </EmptyState>
            );
        }

        return selectedSupplierTransactions?.map(transaction => {
            const isPending = transaction.summary?.pendingAmount > 0;
            const isActive = selectedTransactionId === transaction.id;

            return (
                <TransactionItem
                    key={transaction.id}
                    active={isActive}
                    onClick={() => handleViewTransactionDetail(transaction.id)}
                >
                    <TransactionIcon>
                        {isPending ?
                            <WarningIcon color="warning" sx={{ fontSize: 28 }} /> :
                            <CheckCircleIcon color="success" sx={{ fontSize: 28 }} />
                        }
                    </TransactionIcon>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            mb: 2
                        }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <EventIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                        {formatDateToDDMMYYYY(transaction.date)}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary' }}>
                                {formatCurrency(transaction.summary?.grandTotal || 0)}
                            </Typography>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                            <Chip
                                label={isPending ? 'Pending' : 'Paid'}
                                size="small"
                                sx={{
                                    bgcolor: isPending ? 'warning.main' : 'success.main',
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: 13,
                                }}
                            />
                        </Box>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2.5,
                            flexWrap: 'wrap'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LocalAtmIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                    {formatCurrency(transaction.summary?.paymentDone || 0)}
                                </Typography>
                            </Box>
                            {isPending && (
                                <Chip
                                    icon={<WarningIcon />}
                                    label={`${formatCurrency(transaction.summary?.pendingAmount || 0)} pending`}
                                    size="small"
                                    sx={{
                                        bgcolor: 'warning.main',
                                        color: 'white',
                                        fontWeight: 600,
                                        fontSize: 13,
                                    }}
                                />
                            )}
                            {transaction.summary?.pendingFine > 0 && (
                                <Chip
                                    icon={<ScaleIcon />}
                                    label={`${transaction.summary?.pendingFine || 0}g fine`}
                                    size="small"
                                    sx={{
                                        bgcolor: 'secondary.main',
                                        color: 'white',
                                        fontWeight: 600,
                                        fontSize: 13,
                                    }}
                                />
                            )}
                        </Box>
                    </Box>
                </TransactionItem>
            );
        });
    };

    // Render fine list
    const renderFineList = () => {
        if (!selectedSupplierId) {
            return (
                <EmptyState>
                    <ScaleIcon sx={{ fontSize: 56, mb: 2, opacity: 0.5 }} />
                    Select a supplier to view fine details
                </EmptyState>
            );
        }

        if (fineTransactions?.length === 0) {
            return (
                <EmptyState>
                    <CheckCircleIcon sx={{ fontSize: 56, mb: 2, opacity: 0.5 }} />
                    No fine transactions found for this supplier
                </EmptyState>
            );
        }

        return fineTransactions?.map((transaction, transactionIndex) => {
            return transaction?.finePayments?.map((fine, index) => (
                <FineItem key={`${transaction.id}-${index}`}>
                    <Box sx={{ mb: 2 }}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            {/* <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                Transaction: {transaction.billNo || transaction.transactionDetails?.billNo || 'N/A'}x``
                            </Typography> */}
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                                Date : {formatDateToDDMMYYYY(transaction.date)}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{
                        pt: 2,
                        borderTop: `1px solid ${theme.palette.divider}`
                    }}>
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 2.5
                        }}>
                            <FineStat>
                                <Typography variant="caption" sx={{
                                    display: 'block',
                                    color: 'text.secondary',
                                    mb: 1,
                                    fontWeight: 600,
                                    letterSpacing: 0.5,
                                    textTransform: 'uppercase'
                                }}>
                                    Weight
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary' }}>
                                    {fine.weight}g
                                </Typography>
                            </FineStat>
                            <FineStat>
                                <Typography variant="caption" sx={{
                                    display: 'block',
                                    color: 'text.secondary',
                                    mb: 1,
                                    fontWeight: 600,
                                    letterSpacing: 0.5,
                                    textTransform: 'uppercase'
                                }}>
                                    Purity
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary' }}>
                                    {fine.purity}%
                                </Typography>
                            </FineStat>
                            <FineStat>
                                <Typography variant="caption" sx={{
                                    display: 'block',
                                    color: 'text.secondary',
                                    mb: 1,
                                    fontWeight: 600,
                                    letterSpacing: 0.5,
                                    textTransform: 'uppercase'
                                }}>
                                    Actual Fine
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary' }}>
                                    {fine.actualFine}g
                                </Typography>
                            </FineStat>
                        </Box>
                    </Box>
                </FineItem>
            ));
        });
    };

    const ItemTypeBadge = styled(Box, {
        shouldForwardProp: (prop) => prop !== 'type',
    })(({ theme, type }) => ({
        display: 'inline-flex',
        alignItems: 'center',
        padding: theme.spacing(0.5, 1.5),
        borderRadius: 8,
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        backgroundColor: theme.palette.mode === 'light'
            ? (type === 'piece' ? 'rgba(37, 99, 235, 0.1)' :
                type === 'melting' ? 'rgba(245, 158, 11, 0.1)' :
                    type === 'pure' ? 'rgba(16, 185, 129, 0.1)' :
                        'rgba(139, 92, 246, 0.1)')
            : (type === 'piece' ? 'rgba(96, 165, 250, 0.15)' :
                type === 'melting' ? 'rgba(251, 191, 36, 0.15)' :
                    type === 'pure' ? 'rgba(52, 211, 153, 0.15)' :
                        'rgba(167, 139, 250, 0.15)'),
        color: theme.palette.mode === 'light'
            ? (type === 'piece' ? '#2563EB' :
                type === 'melting' ? '#D97706' :
                    type === 'pure' ? '#059669' :
                        '#7C3AED')
            : (type === 'piece' ? '#60A5FA' :
                type === 'melting' ? '#FBBF24' :
                    type === 'pure' ? '#34D399' :
                        '#A78BFA'),
        border: `1px solid ${theme.palette.mode === 'light'
            ? (type === 'piece' ? 'rgba(37, 99, 235, 0.2)' :
                type === 'melting' ? 'rgba(245, 158, 11, 0.2)' :
                    type === 'pure' ? 'rgba(16, 185, 129, 0.2)' :
                        'rgba(139, 92, 246, 0.2)')
            : (type === 'piece' ? 'rgba(96, 165, 250, 0.3)' :
                type === 'melting' ? 'rgba(251, 191, 36, 0.3)' :
                    type === 'pure' ? 'rgba(52, 211, 153, 0.3)' :
                        'rgba(167, 139, 250, 0.3)')}`
    }));

    // const ItemsTableSection = styled(DetailSection)(({ theme }) => ({
    //     overflow: 'hidden',
    //     padding: theme.spacing(3, 0),
    // }));

    // const ItemsTableHeader = styled(Box)(({ theme }) => ({
    //     padding: theme.spacing(2, 3),
    //     borderBottom: `1px solid ${theme.palette.divider}`,
    // }));

    // const ItemsTableContainer = styled(Box)(({ theme }) => ({
    //     overflowY: 'auto',
    //     maxHeight: '400px',
    //     padding: theme.spacing(0, 3),
    //     '&::-webkit-scrollbar': {
    //         width: '6px',
    //     },
    //     '&::-webkit-scrollbar-track': {
    //         background: theme.palette.mode === 'light'
    //             ? 'rgba(0, 0, 0, 0.05)'
    //             : 'rgba(255, 255, 255, 0.05)',
    //         borderRadius: '10px',
    //     },
    //     '&::-webkit-scrollbar-thumb': {
    //         background: theme.palette.mode === 'light'
    //             ? 'rgba(37, 99, 235, 0.3)'
    //             : 'rgba(96, 165, 250, 0.3)',
    //         borderRadius: '10px',
    //     },
    // }));

    // const ItemRow = styled(Box, {
    //     shouldForwardProp: (prop) => prop !== 'isEven',
    // })(({ theme, isEven }) => ({
    //     display: 'grid',
    //     gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
    //     alignItems: 'center',
    //     padding: theme.spacing(2),
    //     backgroundColor: isEven
    //         ? (theme.palette.mode === 'light'
    //             ? 'rgba(0, 0, 0, 0.02)'
    //             : 'rgba(255, 255, 255, 0.02)')
    //         : 'transparent',
    //     borderBottom: `1px solid ${theme.palette.divider}`,
    //     transition: 'all 0.2s ease',
    //     '&:hover': {
    //         backgroundColor: theme.palette.mode === 'light'
    //             ? 'rgba(37, 99, 235, 0.04)'
    //             : 'rgba(96, 165, 250, 0.04)',
    //         transform: 'translateX(4px)',
    //     },
    // }));

    // const ItemCell = styled(Box)(({ theme }) => ({
    //     padding: theme.spacing(0, 1),
    //     fontSize: '14px',
    //     color: 'text.primary',
    //     whiteSpace: 'nowrap',
    //     overflow: 'hidden',
    //     textOverflow: 'ellipsis',
    // }));

    // const HeaderCell = styled(Box)(({ theme }) => ({
    //     padding: theme.spacing(1),
    //     fontSize: '12px',
    //     fontWeight: 700,
    //     color: theme.palette.mode === 'light' ? '#64748B' : '#94A3B8',
    //     textTransform: 'uppercase',
    //     letterSpacing: '0.5px',
    //     whiteSpace: 'nowrap',
    // }));

    // Render transaction detail
    const renderTransactionDetail = () => {
        if (!selectedTransactionDetail) return null;

        const transaction = selectedTransactionDetail;
        const supplier = selectedSupplier;
        const summary = transaction.summary || {};
        const items = transaction.items || [];
        const payments = transaction.payments || {};
        const finePayments = transaction.finePayments || [];
        const discount = transaction.discount || {};
        // const transactionDetails = transaction.transactionDetails || {};

        return (
            <GlassPanel sx={{ p: 4, maxHeight: '85vh', overflowY: 'auto' }}>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 4,
                    pb: 2.5,
                    borderBottom: `1px solid ${theme.palette.divider}`
                }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => setTransactionDetailOpen(false)}
                        sx={{
                            borderRadius: 12,
                            padding: '10px 24px',
                            bgcolor: theme.palette.mode === 'light'
                                ? 'rgba(255, 255, 255, 0.85)'
                                : 'rgba(30, 41, 59, 0.85)',
                            border: `1px solid ${theme.palette.mode === 'light'
                                ? 'rgba(255, 255, 255, 0.3)'
                                : 'rgba(148, 163, 184, 0.1)'}`,
                            color: 'text.primary',
                            fontWeight: 600,
                            textTransform: 'none',
                            '&:hover': {
                                bgcolor: theme.palette.mode === 'light'
                                    ? 'rgba(37, 99, 235, 0.1)'
                                    : 'rgba(96, 165, 250, 0.1)',
                                borderColor: theme.palette.mode === 'light'
                                    ? 'rgba(37, 99, 235, 0.3)'
                                    : 'rgba(96, 165, 250, 0.3)',
                            }
                        }}
                    >
                        Back to transactions
                    </Button>
                    <Chip
                        label={summary.pendingAmount > 0 ? 'PENDING' : 'PAID'}
                        sx={{
                            fontWeight: 700,
                            letterSpacing: 0.5,
                            padding: '8px 20px',
                            fontSize: 13,
                            height: 32,
                            bgcolor: summary.pendingAmount > 0 ? 'warning.main' : 'success.main',
                            color: 'white',
                        }}
                    />
                </Box>

                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 3
                }}>
                    {/* Supplier Details */}
                    <DetailSection>
                        <Typography variant="h6" sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            mb: 3,
                            color: 'text.primary',
                            pb: 2,
                            borderBottom: `2px solid ${theme.palette.divider}`
                        }}>
                            <BusinessIcon /> Supplier Information
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                py: 1.5,
                                borderBottom: `1px solid ${theme.palette.divider}`
                            }}>
                                <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                    Name
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    {supplier?.name || 'N/A'}
                                </Typography>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                py: 1.5,
                                borderBottom: `1px solid ${theme.palette.divider}`
                            }}>
                                <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                    Contact
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    {supplier?.contact || 'N/A'}
                                </Typography>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                py: 1.5,
                            }}>
                                <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                    GST
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    {supplier?.gstNumber || 'N/A'}
                                </Typography>
                            </Box>
                        </Box>
                    </DetailSection>

                    {/* Transaction Details */}
                    <DetailSection>
                        <Typography variant="h6" sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            mb: 3,
                            color: 'text.primary',
                            pb: 2,
                            borderBottom: `2px solid ${theme.palette.divider}`
                        }}>
                            <ReceiptIcon /> Transaction Details
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                py: 1.5,
                                borderBottom: `1px solid ${theme.palette.divider}`
                            }}>
                                <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                    Date
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    {formatDateToDDMMYYYY(transaction.date)}
                                </Typography>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                py: 1.5,
                                borderBottom: `1px solid ${theme.palette.divider}`
                            }}>
                                <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                    GST Status
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip
                                        label={transaction.gstApplicable ? 'Applicable' : 'Not Applicable'}
                                        size="small"
                                        color={transaction.gstApplicable ? 'success' : 'default'}
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: 13,
                                            height: 28,
                                            backgroundColor: transaction.gstApplicable
                                                ? (theme.palette.mode === 'light'
                                                    ? 'rgba(16, 185, 129, 0.1)'
                                                    : 'rgba(52, 211, 153, 0.1)')
                                                : (theme.palette.mode === 'light'
                                                    ? 'rgba(107, 114, 128, 0.1)'
                                                    : 'rgba(148, 163, 184, 0.1)'),
                                            color: transaction.gstApplicable
                                                ? (theme.palette.mode === 'light' ? '#059669' : '#34D399')
                                                : (theme.palette.mode === 'light' ? '#4B5563' : '#94A3B8'),
                                            border: transaction.gstApplicable
                                                ? `1px solid ${theme.palette.mode === 'light'
                                                    ? 'rgba(16, 185, 129, 0.2)'
                                                    : 'rgba(52, 211, 153, 0.2)'}`
                                                : `1px solid ${theme.palette.mode === 'light'
                                                    ? 'rgba(107, 114, 128, 0.2)'
                                                    : 'rgba(148, 163, 184, 0.2)'}`
                                        }}
                                    />
                                    {transaction.gstApplicable && supplier?.gstNumber && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: 'text.secondary',
                                                fontFamily: 'monospace',
                                                fontSize: 12
                                            }}
                                        >
                                            {supplier.gstNumber}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                            {transaction.gstApplicable && (
                                <Box sx={{
                                    py: 1.5,
                                    px: 2,
                                    borderRadius: 1,
                                    backgroundColor: theme.palette.mode === 'light'
                                        ? 'rgba(16, 185, 129, 0.05)'
                                        : 'rgba(52, 211, 153, 0.05)',
                                    border: `1px solid ${theme.palette.mode === 'light'
                                        ? 'rgba(16, 185, 129, 0.1)'
                                        : 'rgba(52, 211, 153, 0.1)'}`
                                }}>
                                    <Typography variant="caption" sx={{
                                        color: theme.palette.mode === 'light' ? '#059669' : '#34D399',
                                        fontWeight: 600,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5
                                    }}>
                                        <InfoIcon fontSize="small" />
                                        GST Rate: 3% (1.5% CGST + 1.5% SGST)
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </DetailSection>

                    {/* Items Table */}
                    {/* Items Table */}
                    {/* // Instead of one big table, show separate tables for each type */}
                    {/* Items Table */}
                    <DetailSection fullWidth>
                        <Typography variant="h6" sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            mb: 3,
                            color: 'text.primary',
                            pb: 2,
                            borderBottom: `2px solid ${theme.palette.divider}`
                        }}>
                            <InventoryIcon /> Purchase Items ({items?.length})
                        </Typography>

                        {/* Group items by type with accordion */}
                        {(() => {
                            const groupedItems = {
                                piece: items.filter(item => item.type === 'piece'),
                                melting: items.filter(item => item.type === 'melting'),
                                pure: items.filter(item => item.type === 'pure'),
                                fine: items.filter(item => item.type === 'fine')
                            };

                            return Object.entries(groupedItems).map(([type, typeItems]) => {
                                if (typeItems.length === 0) return null;

                                const getTypeDisplay = (type) => {
                                    switch (type) {
                                        case 'piece': return 'On Piece Items';
                                        case 'melting': return 'On Melting Items';
                                        case 'pure': return 'On Pure Items';
                                        case 'fine': return 'By Fine Items';
                                        default: return type;
                                    }
                                };

                                const getTypeIcon = (type) => {
                                    switch (type) {
                                        case 'piece': return <LocalAtmIcon sx={{ fontSize: 16 }} />;
                                        case 'melting': return <WhatshotIcon sx={{ fontSize: 16 }} />;
                                        case 'pure': return <DiamondIcon sx={{ fontSize: 16 }} />;
                                        case 'fine': return <ScaleIcon sx={{ fontSize: 16 }} />;
                                        default: return <InventoryIcon sx={{ fontSize: 16 }} />;
                                    }
                                };

                                const getHeaders = () => {
                                    switch (type) {
                                        case 'piece': return ['Item', 'Metal', 'Pieces', 'Price/PC', 'Amount'];
                                        case 'melting': return ['Item', 'Metal', 'Melting %', 'Rate/g', 'Weight', 'Wastage %', 'Amount'];
                                        case 'pure': return ['Item', 'Metal', 'Rate/g', 'Weight', 'Labour/g', 'Amount'];
                                        case 'fine': return ['Item', 'Metal', 'Weight', 'Melting %', 'Wastage %', 'Fine'];
                                        default: return [];
                                    }
                                };

                                const calculateItemTotal = (item) => {
                                    if (item.type === 'piece') {
                                        return (item.count || 0) * (item.price || 0);
                                    } else if (item.type === 'melting') {
                                        const rate = parseFloat(item.rate) || 0;
                                        const weight = parseFloat(item.weight) || 0;
                                        const wastage = parseFloat(item.wastage) || 0;
                                        const percent = parseFloat(item.percent) || 0;
                                        return ((wastage + percent) / 100) * weight * rate;
                                    } else if (item.type === 'pure') {
                                        const rate = parseFloat(item.rate) || 0;
                                        const weight = parseFloat(item.weight) || 0;
                                        const labour = parseFloat(item.labour) || 0;
                                        return weight * (rate + labour);
                                    } else if (item.type === 'fine') {
                                        const melting = parseFloat(item.melting) || 0;
                                        const wastage = parseFloat(item.wastage) || 0;
                                        const weight = parseFloat(item.weight) || 0;
                                        return ((melting + wastage) / 100) * weight;
                                    }
                                    return 0;
                                };

                                const typeTotal = typeItems.reduce((sum, item) => sum + calculateItemTotal(item), 0);
                                const typeColor = theme.palette.mode === 'light'
                                    ? (type === 'piece' ? '#2563EB' :
                                        type === 'melting' ? '#F59E0B' :
                                            type === 'pure' ? '#10B981' :
                                                '#8B5CF6')
                                    : (type === 'piece' ? '#60A5FA' :
                                        type === 'melting' ? '#FBBF24' :
                                            type === 'pure' ? '#34D399' :
                                                '#A78BFA');

                                return (
                                    <Box key={type} sx={{ mb: 4 }}>
                                        {/* Type Header */}
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            mb: 2,
                                            p: 2,
                                            borderRadius: 2,
                                            backgroundColor: theme.palette.mode === 'light'
                                                ? `${typeColor}08`
                                                : `${typeColor}15`,
                                            border: `1px solid ${theme.palette.mode === 'light'
                                                ? `${typeColor}20`
                                                : `${typeColor}30`}`
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Box sx={{
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: 10,
                                                    backgroundColor: theme.palette.mode === 'light'
                                                        ? `${typeColor}10`
                                                        : `${typeColor}20`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: typeColor,
                                                }}>
                                                    {getTypeIcon(type)}
                                                </Box>
                                                <Box>
                                                    <Typography variant="subtitle1" sx={{
                                                        fontWeight: 700,
                                                        color: typeColor,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        {getTypeDisplay(type)}
                                                        <Chip
                                                            label={`${typeItems.length} items`}
                                                            size="small"
                                                            sx={{
                                                                height: 20,
                                                                fontSize: 11,
                                                                fontWeight: 600,
                                                                backgroundColor: theme.palette.mode === 'light'
                                                                    ? 'rgba(255, 255, 255, 0.9)'
                                                                    : 'rgba(30, 41, 59, 0.9)',
                                                                color: typeColor,
                                                                border: `1px solid ${typeColor}30`
                                                            }}
                                                        />
                                                    </Typography>
                                                    <Typography variant="caption" sx={{
                                                        color: 'text.secondary',
                                                        fontWeight: 500
                                                    }}>
                                                        {type === 'piece' ? 'Items sold by piece count' :
                                                            type === 'melting' ? 'Items based on melting percentage' :
                                                                type === 'pure' ? 'Pure metal items with labour charges' :
                                                                    'Fine weight calculation items'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Typography variant="caption" sx={{
                                                    color: 'text.secondary',
                                                    fontWeight: 600,
                                                    display: 'block',
                                                    mb: 0.5
                                                }}>
                                                    Total
                                                </Typography>
                                                <Typography variant="h6" sx={{
                                                    fontWeight: 800,
                                                    color: typeColor
                                                }}>
                                                    {type === 'fine'
                                                        ? `${typeTotal.toFixed(2)}g`
                                                        : `₹${typeTotal.toLocaleString('en-IN')}`
                                                    }
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Items Table */}
                                        <Box sx={{
                                            overflowX: 'auto',
                                            borderRadius: 2,
                                            border: `1px solid ${theme.palette.divider}`,
                                            backgroundColor: theme.palette.mode === 'light'
                                                ? 'rgba(255, 255, 255, 0.05)'
                                                : 'rgba(30, 41, 59, 0.3)',
                                            position: 'relative'
                                        }}>
                                            {/* Table Header */}
                                            <Box sx={{
                                                display: 'grid',
                                                gridTemplateColumns: `60px repeat(${getHeaders().length}, minmax(120px, 1fr))`,
                                                backgroundColor: theme.palette.mode === 'light'
                                                    ? 'rgba(0, 0, 0, 0.02)'
                                                    : 'rgba(255, 255, 255, 0.02)',
                                                borderBottom: `2px solid ${theme.palette.divider}`,
                                                position: 'sticky',
                                                top: 0,
                                                zIndex: 1
                                            }}>
                                                <Box sx={{
                                                    p: 2,
                                                    textAlign: 'center',
                                                    fontWeight: 700,
                                                    color: theme.palette.mode === 'light' ? '#64748B' : '#94A3B8',
                                                    borderBottom: `2px solid ${theme.palette.divider}`,
                                                    fontSize: 12,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: 0.5,
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    #
                                                </Box>
                                                {getHeaders().map((header) => (
                                                    <Box key={header} sx={{
                                                        p: 2,
                                                        fontWeight: 700,
                                                        color: theme.palette.mode === 'light' ? '#64748B' : '#94A3B8',
                                                        borderBottom: `2px solid ${theme.palette.divider}`,
                                                        fontSize: 12,
                                                        textTransform: 'uppercase',
                                                        letterSpacing: 0.5,
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {header}
                                                    </Box>
                                                ))}
                                            </Box>

                                            {/* Table Body */}
                                            {typeItems.map((item, index) => {
                                                const itemTotal = calculateItemTotal(item);
                                                const isEven = index % 2 === 0;

                                                return (
                                                    <Box key={index} sx={{
                                                        display: 'grid',
                                                        gridTemplateColumns: `60px repeat(${getHeaders().length}, minmax(120px, 1fr))`,
                                                        backgroundColor: isEven
                                                            ? (theme.palette.mode === 'light'
                                                                ? 'rgba(0, 0, 0, 0.02)'
                                                                : 'rgba(255, 255, 255, 0.02)')
                                                            : 'transparent',
                                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': {
                                                            backgroundColor: theme.palette.mode === 'light'
                                                                ? `${typeColor}08`
                                                                : `${typeColor}15`,
                                                        }
                                                    }}>
                                                        {/* Serial Number */}
                                                        <Box sx={{
                                                            p: 2,
                                                            textAlign: 'center',
                                                            fontWeight: 600,
                                                            color: 'text.primary',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}>
                                                            <Box sx={{
                                                                width: 24,
                                                                height: 24,
                                                                borderRadius: '50%',
                                                                backgroundColor: theme.palette.mode === 'light'
                                                                    ? `${typeColor}10`
                                                                    : `${typeColor}20`,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontSize: 12,
                                                                fontWeight: 700,
                                                                color: typeColor
                                                            }}>
                                                                {index + 1}
                                                            </Box>
                                                        </Box>

                                                        {/* Item Name */}
                                                        <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                                                            <Box sx={{ flex: 1 }}>
                                                                <Typography variant="body2" sx={{
                                                                    fontWeight: 600,
                                                                    color: 'text.primary',
                                                                    mb: 0.5
                                                                }}>
                                                                    {item.itemName || 'Unnamed Item'}
                                                                </Typography>
                                                                {item.description && (
                                                                    <Typography variant="caption" sx={{
                                                                        color: 'text.secondary',
                                                                        display: 'block'
                                                                    }}>
                                                                        {item.description}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Box>

                                                        {/* Type-specific columns */}
                                                        {type === 'piece' && (
                                                            <>
                                                                <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                                                                    <ItemTypeBadge type={item.metal?.toLowerCase() || 'other'}>
                                                                        {item.metal?.charAt(0).toUpperCase() + item.metal?.slice(1) || 'N/A'}
                                                                    </ItemTypeBadge>
                                                                </Box>
                                                                <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                                                                    <Box sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 1,
                                                                        backgroundColor: theme.palette.mode === 'light'
                                                                            ? 'rgba(37, 99, 235, 0.1)'
                                                                            : 'rgba(96, 165, 250, 0.1)',
                                                                        padding: '4px 12px',
                                                                        borderRadius: 1,
                                                                        border: `1px solid ${theme.palette.mode === 'light'
                                                                            ? 'rgba(37, 99, 235, 0.2)'
                                                                            : 'rgba(96, 165, 250, 0.2)'}`
                                                                    }}>
                                                                        <NumbersIcon sx={{ fontSize: 14, color: typeColor }} />
                                                                        <Typography variant="body2" sx={{ fontWeight: 700, color: typeColor }}>
                                                                            {item.count || 0}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                                <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                                        ₹{parseFloat(item.price || 0).toLocaleString('en-IN')}
                                                                    </Typography>
                                                                </Box>
                                                                <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                                                                    <Box sx={{
                                                                        backgroundColor: theme.palette.mode === 'light'
                                                                            ? 'rgba(16, 185, 129, 0.1)'
                                                                            : 'rgba(52, 211, 153, 0.1)',
                                                                        padding: '8px 12px',
                                                                        borderRadius: 1,
                                                                        border: `1px solid ${theme.palette.mode === 'light'
                                                                            ? 'rgba(16, 185, 129, 0.2)'
                                                                            : 'rgba(52, 211, 153, 0.2)'}`,
                                                                        width: '100%'
                                                                    }}>
                                                                        <Typography variant="body2" sx={{
                                                                            fontWeight: 800,
                                                                            color: '#059669',
                                                                            textAlign: 'right'
                                                                        }}>
                                                                            ₹{itemTotal.toLocaleString('en-IN')}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </>
                                                        )}

                                                        {type === 'melting' && (
                                                            <>
                                                                <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                                                                    <ItemTypeBadge type={item.metal?.toLowerCase() || 'other'}>
                                                                        {item.metal?.charAt(0).toUpperCase() + item.metal?.slice(1) || 'N/A'}
                                                                    </ItemTypeBadge>
                                                                </Box>
                                                                <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                                                                    <Chip
                                                                        label={`${item.percent || 0}%`}
                                                                        size="small"
                                                                        sx={{
                                                                            backgroundColor: theme.palette.mode === 'light'
                                                                                ? 'rgba(245, 158, 11, 0.1)'
                                                                                : 'rgba(251, 191, 36, 0.1)',
                                                                            color: '#D97706',
                                                                            fontWeight: 700,
                                                                            border: `1px solid ${theme.palette.mode === 'light'
                                                                                ? 'rgba(245, 158, 11, 0.2)'
                                                                                : 'rgba(251, 191, 36, 0.2)'}`
                                                                        }}
                                                                    />
                                                                </Box>
                                                                <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                                        ₹{parseFloat(item.rate || 0).toLocaleString('en-IN')}
                                                                    </Typography>
                                                                </Box>
                                                                <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                                                                    <Chip
                                                                        label={`${parseFloat(item.weight || 0).toFixed(2)}g`}
                                                                        size="small"
                                                                        icon={<ScaleIcon sx={{ fontSize: 14 }} />}
                                                                        sx={{
                                                                            backgroundColor: theme.palette.mode === 'light'
                                                                                ? 'rgba(37, 99, 235, 0.1)'
                                                                                : 'rgba(96, 165, 250, 0.1)',
                                                                            color: '#2563EB',
                                                                            fontWeight: 700,
                                                                            border: `1px solid ${theme.palette.mode === 'light'
                                                                                ? 'rgba(37, 99, 235, 0.2)'
                                                                                : 'rgba(96, 165, 250, 0.2)'}`
                                                                        }}
                                                                    />
                                                                </Box>
                                                                <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                                                                    <Chip
                                                                        label={`${item.wastage || 0}%`}
                                                                        size="small"
                                                                        sx={{
                                                                            backgroundColor: theme.palette.mode === 'light'
                                                                                ? 'rgba(239, 68, 68, 0.1)'
                                                                                : 'rgba(248, 113, 113, 0.1)',
                                                                            color: '#DC2626',
                                                                            fontWeight: 700,
                                                                            border: `1px solid ${theme.palette.mode === 'light'
                                                                                ? 'rgba(239, 68, 68, 0.2)'
                                                                                : 'rgba(248, 113, 113, 0.2)'}`
                                                                        }}
                                                                    />
                                                                </Box>
                                                                <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                                                                    <Box sx={{
                                                                        backgroundColor: theme.palette.mode === 'light'
                                                                            ? 'rgba(16, 185, 129, 0.1)'
                                                                            : 'rgba(52, 211, 153, 0.1)',
                                                                        padding: '8px 12px',
                                                                        borderRadius: 1,
                                                                        border: `1px solid ${theme.palette.mode === 'light'
                                                                            ? 'rgba(16, 185, 129, 0.2)'
                                                                            : 'rgba(52, 211, 153, 0.2)'}`,
                                                                        width: '100%'
                                                                    }}>
                                                                        <Typography variant="body2" sx={{
                                                                            fontWeight: 800,
                                                                            color: '#059669',
                                                                            textAlign: 'right'
                                                                        }}>
                                                                            ₹{itemTotal.toLocaleString('en-IN')}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </>
                                                        )}

                                                        {type === 'pure' && (
                                                            <>
                                                                <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                                                                    <ItemTypeBadge type={item.metal?.toLowerCase() || 'other'}>
                                                                        {item.metal?.charAt(0).toUpperCase() + item.metal?.slice(1) || 'N/A'}
                                                                    </ItemTypeBadge>
                                                                </Box>
                                                                <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                                        ₹{parseFloat(item.rate || 0).toLocaleString('en-IN')}
                                                                    </Typography>
                                                                </Box>
                                                                <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                                                                    <Chip
                                                                        label={`${parseFloat(item.weight || 0).toFixed(2)}g`}
                                                                        size="small"
                                                                        icon={<ScaleIcon sx={{ fontSize: 14 }} />}
                                                                        sx={{
                                                                            backgroundColor: theme.palette.mode === 'light'
                                                                                ? 'rgba(37, 99, 235, 0.1)'
                                                                                : 'rgba(96, 165, 250, 0.1)',
                                                                            color: '#2563EB',
                                                                            fontWeight: 700,
                                                                            border: `1px solid ${theme.palette.mode === 'light'
                                                                                ? 'rgba(37, 99, 235, 0.2)'
                                                                                : 'rgba(96, 165, 250, 0.2)'}`
                                                                        }}
                                                                    />
                                                                </Box>
                                                                <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                                        ₹{parseFloat(item.labour || 0).toLocaleString('en-IN')}
                                                                    </Typography>
                                                                </Box>
                                                                <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                                                                    <Box sx={{
                                                                        backgroundColor: theme.palette.mode === 'light'
                                                                            ? 'rgba(16, 185, 129, 0.1)'
                                                                            : 'rgba(52, 211, 153, 0.1)',
                                                                        padding: '8px 12px',
                                                                        borderRadius: 1,
                                                                        border: `1px solid ${theme.palette.mode === 'light'
                                                                            ? 'rgba(16, 185, 129, 0.2)'
                                                                            : 'rgba(52, 211, 153, 0.2)'}`,
                                                                        width: '100%'
                                                                    }}>
                                                                        <Typography variant="body2" sx={{
                                                                            fontWeight: 800,
                                                                            color: '#059669',
                                                                            textAlign: 'right'
                                                                        }}>
                                                                            ₹{itemTotal.toLocaleString('en-IN')}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </>
                                                        )}

                                                        {type === 'fine' && (
                                                            <>
                                                                <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                                                                    <ItemTypeBadge type={item.metal?.toLowerCase() || 'other'}>
                                                                        {item.metal?.charAt(0).toUpperCase() + item.metal?.slice(1) || 'N/A'}
                                                                    </ItemTypeBadge>
                                                                </Box>
                                                                <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                                                                    <Chip
                                                                        label={`${parseFloat(item.weight || 0).toFixed(2)}g`}
                                                                        size="small"
                                                                        icon={<ScaleIcon sx={{ fontSize: 14 }} />}
                                                                        sx={{
                                                                            backgroundColor: theme.palette.mode === 'light'
                                                                                ? 'rgba(37, 99, 235, 0.1)'
                                                                                : 'rgba(96, 165, 250, 0.1)',
                                                                            color: '#2563EB',
                                                                            fontWeight: 700,
                                                                            border: `1px solid ${theme.palette.mode === 'light'
                                                                                ? 'rgba(37, 99, 235, 0.2)'
                                                                                : 'rgba(96, 165, 250, 0.2)'}`
                                                                        }}
                                                                    />
                                                                </Box>
                                                                <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                                                                    <Chip
                                                                        label={`${item.melting || 0}%`}
                                                                        size="small"
                                                                        sx={{
                                                                            backgroundColor: theme.palette.mode === 'light'
                                                                                ? 'rgba(245, 158, 11, 0.1)'
                                                                                : 'rgba(251, 191, 36, 0.1)',
                                                                            color: '#D97706',
                                                                            fontWeight: 700,
                                                                            border: `1px solid ${theme.palette.mode === 'light'
                                                                                ? 'rgba(245, 158, 11, 0.2)'
                                                                                : 'rgba(251, 191, 36, 0.2)'}`
                                                                        }}
                                                                    />
                                                                </Box>
                                                                <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                                                                    <Chip
                                                                        label={`${item.wastage || 0}%`}
                                                                        size="small"
                                                                        sx={{
                                                                            backgroundColor: theme.palette.mode === 'light'
                                                                                ? 'rgba(239, 68, 68, 0.1)'
                                                                                : 'rgba(248, 113, 113, 0.1)',
                                                                            color: '#DC2626',
                                                                            fontWeight: 700,
                                                                            border: `1px solid ${theme.palette.mode === 'light'
                                                                                ? 'rgba(239, 68, 68, 0.2)'
                                                                                : 'rgba(248, 113, 113, 0.2)'}`
                                                                        }}
                                                                    />
                                                                </Box>
                                                                <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                                                                    <Box sx={{
                                                                        backgroundColor: theme.palette.mode === 'light'
                                                                            ? 'rgba(245, 158, 11, 0.1)'
                                                                            : 'rgba(251, 191, 36, 0.1)',
                                                                        padding: '8px 12px',
                                                                        borderRadius: 1,
                                                                        border: `1px solid ${theme.palette.mode === 'light'
                                                                            ? 'rgba(245, 158, 11, 0.2)'
                                                                            : 'rgba(251, 191, 36, 0.2)'}`,
                                                                        width: '100%'
                                                                    }}>
                                                                        <Typography variant="body2" sx={{
                                                                            fontWeight: 800,
                                                                            color: '#D97706',
                                                                            textAlign: 'right'
                                                                        }}>
                                                                            {itemTotal.toFixed(2)}g
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </>
                                                        )}
                                                    </Box>
                                                );
                                            })}

                                            {/* Type Total Row */}
                                            <Box sx={{
                                                display: 'flex',
                                                // gridTemplateColumns: `60px 1fr repeat(${getHeaders().length - 2}, minmax(120px, 1fr))`,
                                                backgroundColor: theme.palette.mode === 'light'
                                                    ? `${typeColor}08`
                                                    : `${typeColor}15`,
                                                borderTop: `2px solid ${typeColor}30`,
                                                padding: theme.spacing(2)
                                            }}>
                                                <Box>
                                                    <Typography variant="body2" sx={{
                                                        fontWeight: 700,
                                                        marginRight: 2,
                                                        color: 'text.secondary'
                                                    }}>
                                                        {getTypeDisplay(type)} Total:
                                                    </Typography>
                                                </Box>
                                                {Array.from({ length: getHeaders().length - 2 }).map((_, i) => (
                                                    <Box key={i}></Box>
                                                ))}
                                                <Box>
                                                    <Typography variant="h6" sx={{
                                                        fontWeight: 800,
                                                        color: typeColor,
                                                        textAlign: 'right',
                                                    }}>
                                                        {type === 'fine'
                                                            ? `${typeTotal.toFixed(2)}g`
                                                            : `₹${typeTotal.toLocaleString('en-IN')}`
                                                        }
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                );
                            }).filter(Boolean);
                        })()}
                    </DetailSection>

                    {/* Payment Methods */}
                    <DetailSection>
                        <Typography variant="h6" sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            mb: 3,
                            color: 'text.primary',
                            pb: 2,
                            borderBottom: `2px solid ${theme.palette.divider}`
                        }}>
                            <CreditCardIcon /> Payment Methods
                        </Typography>
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: 2.5
                        }}>
                            <PaymentMethod>
                                <PaymentIcon type="cash">
                                    <LocalAtmIcon />
                                </PaymentIcon>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" sx={{
                                        color: 'text.secondary',
                                        mb: 0.5,
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.5
                                    }}>
                                        Cash
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary' }}>
                                        {formatCurrency(payments.cash || 0)}
                                    </Typography>
                                </Box>
                            </PaymentMethod>
                            <PaymentMethod>
                                <PaymentIcon type="bank">
                                    <BalanceIcon />
                                </PaymentIcon>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" sx={{
                                        color: 'text.secondary',
                                        mb: 0.5,
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.5
                                    }}>
                                        Bank
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary' }}>
                                        {formatCurrency(payments.bank || 0)}
                                    </Typography>
                                </Box>
                            </PaymentMethod>
                        </Box>
                    </DetailSection>

                    {/* Financial Summary */}
                    <DetailSection>
                        <Typography variant="h6" sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            mb: 3,
                            color: 'text.primary',
                            pb: 2,
                            borderBottom: `2px solid ${theme.palette.divider}`
                        }}>
                            <AssessmentIcon /> Financial Summary
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                py: 1.5,
                                borderBottom: `1px solid ${theme.palette.divider}`
                            }}>
                                <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                    Subtotal
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    {formatCurrency(summary.subTotal || transaction.calculatedTotals?.subtotal || 0)}
                                </Typography>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                py: 1.5,
                                borderBottom: `1px solid ${theme.palette.divider}`
                            }}>
                                <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                    CGST (1.5%)
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    {formatCurrency(summary.cgst || transaction.calculatedTotals?.subtotal || 0)}
                                </Typography>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                py: 1.5,
                                borderBottom: `1px solid ${theme.palette.divider}`
                            }}>
                                <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                    SGST (1.5%)
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    {formatCurrency(summary.sgst || transaction.calculatedTotals?.subtotal || 0)}
                                </Typography>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                py: 1.5,
                                borderBottom: `1px solid ${theme.palette.divider}`
                            }}>
                                <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                    Discount
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: 'error.main' }}>
                                    -{formatCurrency(discount.amount || 0)}
                                </Typography>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                py: 2,
                                mt: 2,
                                borderTop: `2px solid ${theme.palette.divider}`
                            }}>
                                <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700 }}>
                                    Grand Total
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary' }}>
                                    {formatCurrency(summary.grandTotal || 0)}
                                </Typography>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                py: 1.5,
                                borderBottom: `1px solid ${theme.palette.divider}`
                            }}>
                                <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                    Payment Done
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 700, color: 'success.main' }}>
                                    {formatCurrency(summary.paymentDone || 0)}
                                </Typography>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                py: 1.5,
                            }}>
                                <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                    Pending Amount
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 700, color: 'warning.main' }}>
                                    {formatCurrency(summary.pendingAmount || 0)}
                                </Typography>
                            </Box>
                        </Box>
                    </DetailSection>

                    {/* Fine Summary */}
                    {finePayments?.length > 0 && (
                        <DetailSection fullWidth>
                            <Typography variant="h6" sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                mb: 3,
                                color: 'text.primary',
                                pb: 2,
                                borderBottom: `2px solid ${theme.palette.divider}`
                            }}>
                                <DiamondIcon /> Fine Summary
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {finePayments?.map((fine, index) => (
                                    <Box key={index} sx={{
                                        p: 2.5,
                                        background: theme.palette.mode === 'light'
                                            ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.05) 100%)'
                                            : 'linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%)',
                                        borderRadius: 2,
                                        border: `1px solid ${theme.palette.mode === 'light'
                                            ? 'rgba(245, 158, 11, 0.2)'
                                            : 'rgba(251, 191, 36, 0.2)'}`
                                    }}>
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            py: 1.5,
                                            borderBottom: `1px solid ${theme.palette.mode === 'light'
                                                ? 'rgba(245, 158, 11, 0.1)'
                                                : 'rgba(251, 191, 36, 0.1)'}`
                                        }}>
                                            <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                                Weight
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                                {fine.weight}g
                                            </Typography>
                                        </Box>
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            py: 1.5,
                                            borderBottom: `1px solid ${theme.palette.mode === 'light'
                                                ? 'rgba(245, 158, 11, 0.1)'
                                                : 'rgba(251, 191, 36, 0.1)'}`
                                        }}>
                                            <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                                Purity
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                                {fine.purity}%
                                            </Typography>
                                        </Box>
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            py: 1.5,
                                        }}>
                                            <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                                Fine Weight
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                                {fine.actualFine}g
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </DetailSection>
                    )}
                </Box>
            </GlassPanel>
        );
    };

    if (loading && suppliers?.length === 0) {
        return (
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
            }}>
                <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress size={60} thickness={4} sx={{ color: 'primary.main', mb: 3 }} />
                    <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                        Loading supplier data...
                    </Typography>
                </Box>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                gap: 3,
                p: 3,
            }}>
                <Alert severity="error" sx={{
                    bgcolor: theme.palette.mode === 'light'
                        ? 'rgba(255, 255, 255, 0.85)'
                        : 'rgba(30, 41, 59, 0.85)',
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${theme.palette.mode === 'light'
                        ? 'rgba(255, 255, 255, 0.3)'
                        : 'rgba(148, 163, 184, 0.1)'}`,
                    borderRadius: 20,
                }}>
                    {error}
                </Alert>
                <Button
                    variant="contained"
                    onClick={refresh}
                    startIcon={<RefreshIcon />}
                    sx={{
                        borderRadius: 12,
                        background: theme.palette.mode === 'light'
                            ? 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)'
                            : 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
                        color: 'white',
                        fontWeight: 600,
                        padding: '12px 32px',
                        boxShadow: theme.shadows[4],
                    }}
                >
                    Retry
                </Button>
            </Box>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{
                minHeight: '100vh',
                p: 2.5,
                position: 'relative',
                overflowX: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: -1,
                    pointerEvents: 'none',
                }
            }}>
                {/* Main Layout */}
                <Box sx={{
                    display: 'flex',
                    gap: 3,
                    height: 'calc(100vh - 80px)',
                    [theme.breakpoints.down('lg')]: {
                        flexDirection: 'column',
                    }
                }}>
                    {/* Supplier List Sidebar */}
                    <GlassPanel sx={{
                        width: 380,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        [theme.breakpoints.down('lg')]: {
                            width: '100%',
                            height: 400,
                        }
                    }}>
                        <Box sx={{
                            p: 2.5,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <BusinessIcon sx={{ color: 'primary.main' }} />
                                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                    Suppliers with Pending Payments
                                </Typography>
                                <Chip
                                    label={`${suppliersWithPendingPayments?.length || 0} suppliers`}
                                    size="small"
                                    sx={{
                                        fontWeight: 600,
                                        background: theme.palette.mode === 'light'
                                            ? 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)'
                                            : 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
                                        color: 'white',
                                        height: 24,
                                        mr: 2
                                    }}
                                />
                            </Box>
                            <IconButton
                                size="small"
                                onClick={() => setShowFilters(!showFilters)}
                                sx={{
                                    bgcolor: showFilters
                                        ? (theme.palette.mode === 'light'
                                            ? 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)'
                                            : 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)')
                                        : (theme.palette.mode === 'light'
                                            ? 'rgba(255, 255, 255, 0.85)'
                                            : 'rgba(30, 41, 59, 0.85)'),
                                    border: `1px solid ${theme.palette.mode === 'light'
                                        ? 'rgba(255, 255, 255, 0.3)'
                                        : 'rgba(148, 163, 184, 0.1)'}`,
                                    color: showFilters ? 'Blue' : 'Grey',
                                }}
                            >
                                <FilterAltIcon />
                            </IconButton>
                        </Box>

                        {showFilters && (
                            <GlassCard sx={{ m: 2, p: 2 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Search suppliers..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 12,
                                            background: theme.palette.mode === 'light'
                                                ? 'rgba(255, 255, 255, 0.05)'
                                                : 'rgba(30, 41, 59, 0.3)',
                                            transition: 'all 0.3s ease',
                                            color: 'text.primary',
                                            '&:hover': {
                                                background: theme.palette.mode === 'light'
                                                    ? 'rgba(255, 255, 255, 0.1)'
                                                    : 'rgba(30, 41, 59, 0.4)',
                                            },
                                            '&.Mui-focused': {
                                                background: theme.palette.mode === 'light'
                                                    ? 'rgba(255, 255, 255, 0.15)'
                                                    : 'rgba(30, 41, 59, 0.5)',
                                                boxShadow: `0 0 0 3px ${theme.palette.mode === 'light'
                                                    ? 'rgba(37, 99, 235, 0.1)'
                                                    : 'rgba(96, 165, 250, 0.1)'}`,
                                            }
                                        },
                                        '& .MuiInputBase-input': {
                                            color: 'text.primary !important',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: `${theme.palette.divider} !important`,
                                        }
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: 'text.secondary' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <Button
                                    fullWidth
                                    size="small"
                                    startIcon={<SortIcon />}
                                    onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                                    sx={{
                                        mt: 2,
                                        borderRadius: 12,
                                        border: `1px solid ${theme.palette.divider}`,
                                        color: 'text.primary',
                                        bgcolor: theme.palette.mode === 'light'
                                            ? 'rgba(255, 255, 255, 0.05)'
                                            : 'rgba(30, 41, 59, 0.3)',
                                        '&:hover': {
                                            bgcolor: theme.palette.mode === 'light'
                                                ? 'rgba(37, 99, 235, 0.1)'
                                                : 'rgba(96, 165, 250, 0.1)',
                                            borderColor: theme.palette.mode === 'light'
                                                ? 'rgba(37, 99, 235, 0.3)'
                                                : 'rgba(96, 165, 250, 0.3)',
                                        }
                                    }}
                                >
                                    Sort: {sortOrder === 'desc' ? 'High to Low (Pending)' : 'Low to High (Pending)'}
                                </Button>
                            </GlassCard>
                        )}

                        <Box sx={{
                            flex: 1,
                            overflowY: 'auto',
                            p: 2
                        }}>
                            {renderSupplierList()}
                        </Box>
                    </GlassPanel>

                    {/* Main Content */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        {/* Header */}
                        <GlassPanel sx={{ p: 2.5 }}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                width: '100%'
                            }}>
                                <Box>
                                    <Typography variant="h4" sx={{
                                        fontWeight: 800,
                                        mb: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        fontSize: 25,
                                        background: theme.palette.mode === 'light'
                                            ? 'linear-gradient(135deg, #8B5CF6 0%, #3B82F6 100%)'
                                            : 'linear-gradient(135deg, #A78BFA 0%, #60A5FA 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                    }}>
                                        <ReceiptIcon sx={{ fontSize: 28 }} />
                                        Supplier Ledger
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: 13, fontWeight: 500 }}>
                                        Pending Payments & Fine Tracking
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<RefreshIcon />}
                                        onClick={refresh}
                                        disabled={loading}
                                        sx={{
                                            borderRadius: 12,
                                            border: `1px solid ${theme.palette.mode === 'light'
                                                ? 'rgba(255, 255, 255, 0.3)'
                                                : 'rgba(148, 163, 184, 0.1)'}`,
                                            color: 'text.primary',
                                            fontWeight: 600,
                                            padding: '8px 20px',
                                            bgcolor: theme.palette.mode === 'light'
                                                ? 'rgba(255, 255, 255, 0.85)'
                                                : 'rgba(30, 41, 59, 0.85)',
                                            '&:hover': {
                                                bgcolor: theme.palette.mode === 'light'
                                                    ? 'rgba(37, 99, 235, 0.1)'
                                                    : 'rgba(96, 165, 250, 0.1)',
                                                borderColor: theme.palette.mode === 'light'
                                                    ? 'rgba(37, 99, 235, 0.3)'
                                                    : 'rgba(96, 165, 250, 0.3)',
                                            }
                                        }}
                                    >
                                        {loading ? 'Refreshing...' : 'Refresh'}
                                    </Button>
                                    {selectedSupplier && (
                                        <Chip
                                            icon={<BusinessIcon />}
                                            label={selectedSupplier.name || 'Select Supplier'}
                                            sx={{
                                                background: theme.palette.mode === 'light'
                                                    ? 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)'
                                                    : 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
                                                color: 'white',
                                                fontWeight: 600,
                                                height: 36,
                                                fontSize: 14,
                                            }}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </GlassPanel>

                        {/* Stats Cards */}
                        <Box    >
                            {renderStatsCards()}
                        </Box>

                        {/* Tabs and Content */}
                        <GlassPanel sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            <Box sx={{
                                p: 0,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                            }}>
                                <Tabs
                                    value={activeTab === 'transactions' ? 0 : 1}
                                    onChange={handleTabChange}
                                    sx={{
                                        minHeight: 56,
                                        '& .MuiTab-root': {
                                            color: 'text.secondary',
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            fontSize: 14,
                                            minHeight: 56,
                                        },
                                        '& .MuiTab-root.Mui-selected': {
                                            color: 'text.primary',
                                        },
                                        '& .MuiTabs-indicator': {
                                            background: theme.palette.mode === 'light'
                                                ? 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)'
                                                : 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
                                            height: 3,
                                            borderRadius: '3px 3px 0 0',
                                        }
                                    }}
                                >
                                    <Tab
                                        label="Transaction History"
                                        icon={<ReceiptLongIcon />}
                                        iconPosition="start"
                                    />
                                    <Tab
                                        label="Fine Details"
                                        icon={<ScaleIcon />}
                                        iconPosition="start"
                                    />
                                </Tabs>
                            </Box>

                            <Box sx={{
                                flex: 1,
                                overflowY: 'auto',
                                p: 3
                            }}>
                                {activeTab === 'transactions' ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {renderTransactionList()}
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {renderFineList()}
                                    </Box>
                                )}
                            </Box>
                        </GlassPanel>
                    </Box>
                </Box>

                {/* Transaction Detail Dialog */}
                <Dialog
                    open={transactionDetailOpen}
                    onClose={() => setTransactionDetailOpen(false)}
                    maxWidth="lg"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 2,
                            overflow: 'hidden',
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                            maxHeight: '90vh',
                        }
                    }}
                >
                    <DialogContent sx={{ p: 0, backgroundColor: 'transparent' }}>
                        {renderTransactionDetail()}
                    </DialogContent>
                </Dialog>
            </Box>
        </LocalizationProvider>
    );
};

export default SupplierLedger;